// Iter41 — Multi-entry sorting result form
//
// Shared event fields above the table:
//   SR + Location + Start/End + Sorter count + Photos + Report PDF
//
// Multi-row entries table:
//   columns: Batch # · Inspector · OK · NOK · REW · [dynamic defect-type cols] · Row total · Rate %
//   controls: + Add row, ✕ delete per row
//   defect-type columns added dynamically via "+ Add defect type"
//
// Footer totals: Σ OK · Σ NOK · Σ REW · Overall rate % · Entries count · Distinct inspectors · Distinct batches
//
// Effectiveness slider REMOVED.
//
// Submit flow:
//   1. POST /api/service-requests/{srId}/sorting-results  (entries[] payload)
//   2. If photos/pdf: POST /api/incidents/{incidentId}/attachments?source=sorting_result:<id>
//   3. PATCH /api/sorting-results/{id} with photos:[ids], report_pdf:id
import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { useT } from "../lib/i18n";
import { useStore } from "../store";
import { NewMaterialMovementModal } from "./NewMaterialMovementModal";
import { DefectBreakdownModal } from "./DefectBreakdownModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCATIONS = [
  { value: "on_site", label: "On site" },
  { value: "facility", label: "Facility" },
  { value: "supplier", label: "Supplier" },
];

const PHOTO_EXT = /\.(jpe?g|png|gif|webp|heic|heif)$/i;
const PDF_EXT = /\.pdf$/i;
const MAX_PHOTOS = 5;

const CANONICAL_DEFECTS = [
  "scratch", "dimensional", "missing_clip", "surface", "paint",
  "weld", "assembly_error", "contamination", "label_missing", "other",
];

const emptyEntry = () => ({
  batch_number: "",
  inspector_name: "",
  total_qty: "",
  ok_qty: "",
  nok_qty: "",
  rework_qty: "",
  defects: {}, // {type: qty_string}
});

const rateColor = (r) => (r > 10 ? "#E24B4A" : r > 5 ? "#f3b14a" : "#4ed084");

const toInt = (v) => {
  const n = parseInt(v || 0, 10);
  return Number.isFinite(n) ? n : 0;
};
const sumOkNokRew = (e) => toInt(e.ok_qty) + toInt(e.nok_qty) + toInt(e.rework_qty);
const effectiveTotal = (e) => (e.total_qty !== "" && e.total_qty != null ? toInt(e.total_qty) : sumOkNokRew(e));
const calcRowRate = (e) => {
  const t = effectiveTotal(e);
  if (t <= 0) return 0;
  return (toInt(e.nok_qty) / t) * 100;
};
const sumDefects = (e) => Object.values(e.defects || {}).reduce((s, v) => s + toInt(v), 0);

export const SortingResultForm = ({ srId, incidentId, onSubmitted, onCancel }) => {
  const { t } = useT();
  const addToast = useStore((s) => s.addToast);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Shared event fields
  const [location, setLocation] = useState("on_site");
  const [startDt, setStartDt] = useState("");
  const [endDt, setEndDt] = useState("");
  const [sorterCount, setSorterCount] = useState(1);

  // Entries
  const [entries, setEntries] = useState([emptyEntry()]);
  const [defectCols, setDefectCols] = useState([]); // string[] — defect-type columns

  // Attachments
  const [photos, setPhotos] = useState([]);
  const [reportPdf, setReportPdf] = useState(null);
  const photoRef = useRef(null);
  const pdfRef = useRef(null);

  // Iter56 — chain a material movement request after submission.
  const [requestMovement, setRequestMovement] = useState(false);
  const [moveModalPrefill, setMoveModalPrefill] = useState(null);

  // Iter79 — defect-breakdown popup state (per-entry editor).
  const [breakdownIdx, setBreakdownIdx] = useState(null);

  // Computed footer totals
  const totals = useMemo(() => {
    let ok = 0, nok = 0, rew = 0, grandTotal = 0;
    const batches = new Set(), inspectors = new Set();
    const defectsByType = {};
    for (const e of entries) {
      ok += toInt(e.ok_qty); nok += toInt(e.nok_qty); rew += toInt(e.rework_qty);
      grandTotal += effectiveTotal(e);
      if ((e.batch_number || "").trim()) batches.add(e.batch_number.trim());
      if ((e.inspector_name || "").trim()) inspectors.add(e.inspector_name.trim());
      for (const [type, qty] of Object.entries(e.defects || {})) {
        defectsByType[type] = (defectsByType[type] || 0) + toInt(qty);
      }
    }
    const rate = grandTotal > 0 ? (nok / grandTotal) * 100 : 0;
    return { ok, nok, rew, grandTotal, rate, batches: batches.size, inspectors: inspectors.size, entryCount: entries.length, defectsByType };
  }, [entries]);

  const updateEntry = (idx, patch) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };
  const updateDefect = (idx, type, value) => {
    setEntries((prev) => prev.map((e, i) => {
      if (i !== idx) return e;
      const nd = { ...e.defects, [type]: value };
      return { ...e, defects: nd };
    }));
  };
  const addRow = () => setEntries((prev) => [...prev, emptyEntry()]);
  const removeRow = (idx) => setEntries((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const addDefectCol = (type) => {
    const clean = (type || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (!clean) return;
    if (defectCols.includes(clean)) return;
    setDefectCols((p) => [...p, clean]);
  };
  const removeDefectCol = (type) => {
    setDefectCols((p) => p.filter((x) => x !== type));
    setEntries((prev) => prev.map((e) => {
      const { [type]: _drop, ...rest } = e.defects;
      return { ...e, defects: rest };
    }));
  };

  // Attachments
  const onPhotoPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const valid = picked.filter((f) => PHOTO_EXT.test(f.name));
    if (valid.length < picked.length) setError(t("sorting.form.err_photo_type"));
    setPhotos((prev) => {
      const merged = [...prev];
      for (const f of valid) {
        if (merged.length >= MAX_PHOTOS) break;
        if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
      }
      return merged;
    });
    if (photoRef.current) photoRef.current.value = "";
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));
  const onPdfPick = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!PDF_EXT.test(f.name)) {
      setError(t("sorting.form.err_pdf_type"));
      if (pdfRef.current) pdfRef.current.value = "";
      return;
    }
    setReportPdf(f);
    if (pdfRef.current) pdfRef.current.value = "";
  };

  const uploadAttachments = async (sortId, incId) => {
    if (!incId) return { photoIds: [], pdfId: null };
    const photoIds = [];
    let pdfId = null;
    if (photos.length) {
      const fd = new FormData();
      photos.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(
        `${API}/incidents/${incId}/attachments?source=sorting_result:${encodeURIComponent(sortId)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      (data.saved || []).forEach((a) => photoIds.push(a.id));
    }
    if (reportPdf) {
      const fd = new FormData();
      fd.append("files", reportPdf);
      const { data } = await axios.post(
        `${API}/incidents/${incId}/attachments?source=sorting_result:${encodeURIComponent(sortId)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      pdfId = (data.saved?.[0]?.id) || null;
    }
    return { photoIds, pdfId };
  };

  const submit = async () => {
    setError("");
    // Validate at least one entry has OK + NOK
    const ready = entries.filter((e) => e.ok_qty !== "" && e.nok_qty !== "");
    if (!ready.length) {
      setError("At least one entry row needs OK qty and NOK qty.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        sorting_location: location,
        start_datetime: startDt ? new Date(startDt).toISOString() : null,
        end_datetime: endDt ? new Date(endDt).toISOString() : null,
        sorter_count: parseInt(sorterCount, 10) || 1,
        entries: ready.map((e) => ({
          batch_number: (e.batch_number || "").trim() || "—",
          inspector_name: (e.inspector_name || "").trim() || "—",
          total_qty: e.total_qty !== "" && e.total_qty != null ? toInt(e.total_qty) : null,
          ok_qty: toInt(e.ok_qty),
          nok_qty: toInt(e.nok_qty),
          rework_qty: toInt(e.rework_qty || 0),
          defect_breakdown: Object.fromEntries(
            Object.entries(e.defects)
              .map(([k, v]) => [k, toInt(v)])
              .filter(([, v]) => v > 0),
          ),
        })),
      };

      const { data: result } = await axios.post(`${API}/service-requests/${srId}/sorting-results`, payload);

      let finalResult = result;
      const incId = incidentId || result.incident_id;
      if ((photos.length || reportPdf) && incId) {
        const { photoIds, pdfId } = await uploadAttachments(result.id, incId);
        if (photoIds.length || pdfId) {
          const patch = {};
          if (photoIds.length) patch.photos = photoIds;
          if (pdfId) patch.report_pdf = pdfId;
          const { data: patched } = await axios.patch(`${API}/sorting-results/${result.id}`, patch);
          finalResult = patched;
        }
      }
      addToast({ type: "success", title: "Sorting result submitted", body: `${result.id} · ${(result.overall_defect_rate_pct || 0).toFixed(1)}%` });
      if (requestMovement) {
        // Open the movement modal pre-filled with the sorting context — defer
        // onSubmitted until the user finishes (or skips) the movement modal.
        setMoveModalPrefill({
          sorting_result_id: finalResult.id,
          incident_id: incId || null,
          direction: "from_sorting",
          from_location: "Sorting bay",
          part_number: null,
          quantity: finalResult.total_ok || null,
          note: `Pickup of sorted material from ${finalResult.id}`,
          _onClosed: () => onSubmitted?.(finalResult),
        });
      } else {
        onSubmitted?.(finalResult);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md p-4"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="sorting-result-form"
    >
      {/* Title */}
      <div className="mb-3 flex items-baseline gap-2" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
        Submit sorting result
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }} data-testid="sorting-form-sr">{srId}</span>
      </div>

      {/* Shared event fields */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Field label="Location">
          <select value={location} onChange={(e) => setLocation(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid="sorting-form-location">
            {LOCATIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </Field>
        <Field label="Sorter count (people)">
          <input type="number" min="1" max="200" value={sorterCount} onChange={(e) => setSorterCount(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid="sorting-form-sorter-count" />
        </Field>
        <Field label="Start">
          <input type="datetime-local" value={startDt} onChange={(e) => setStartDt(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid="sorting-form-start" />
        </Field>
        <Field label="End">
          <input type="datetime-local" value={endDt} onChange={(e) => setEndDt(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()} data-testid="sorting-form-end" />
        </Field>
      </div>

      {/* Entries table */}
      <div className="mb-2 flex items-center justify-between">
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "1.2px", color: "#9198b0", textTransform: "uppercase" }}>
          Entries — one row per (batch × inspector)
        </span>
        <div className="flex items-center gap-1.5">
          <DefectColAdder onAdd={addDefectCol} existing={defectCols} />
          <button
            type="button"
            onClick={addRow}
            className="px-2.5 py-1 rounded"
            style={{ background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec", fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "0.4px", cursor: "pointer" }}
            data-testid="sorting-form-add-row-btn"
          >
            ＋ Add row
          </button>
        </div>
      </div>

      <div className="rounded-md overflow-x-auto" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="sorting-form-entries-table">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 + defectCols.length * 90 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <Th>#</Th>
              <Th>Batch #</Th>
              <Th>Inspector</Th>
              <Th>Total</Th>
              <Th>OK</Th>
              <Th>NOK</Th>
              <Th>REW</Th>
              <Th>Detail</Th>
              {defectCols.map((d) => (
                <Th key={d}>
                  <span className="flex items-center gap-1 justify-end">
                    {d}
                    <button
                      type="button"
                      onClick={() => removeDefectCol(d)}
                      style={{ color: "#ff9a99", cursor: "pointer", fontSize: 11, background: "transparent", border: "none", padding: 0 }}
                      data-testid={`sorting-form-defect-col-remove-${d}`}
                      title="Remove column"
                    >×</button>
                  </span>
                </Th>
              ))}
              <Th>Rate %</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              const tot = effectiveTotal(e);
              const okNokRew = sumOkNokRew(e);
              const totalMismatch = (e.total_qty !== "" && e.total_qty != null) && tot !== okNokRew;
              const defectsSum = sumDefects(e);
              const defectMismatch = defectsSum !== toInt(e.nok_qty) && (defectsSum > 0 || toInt(e.nok_qty) > 0);
              const rate = calcRowRate(e);
              return (
                <React.Fragment key={i}>
                  <tr data-testid={`sorting-form-entry-row-${i}`} style={{ borderBottom: (totalMismatch || defectMismatch) ? "none" : "1px solid rgba(255,255,255,0.05)" }}>
                    <Td><span style={mono(11)}>{i + 1}</span></Td>
                    <Td><RowInput value={e.batch_number} onChange={(v) => updateEntry(i, { batch_number: v })} testId={`sorting-form-batch-${i}`} placeholder="LOT-…" /></Td>
                    <Td><RowInput value={e.inspector_name} onChange={(v) => updateEntry(i, { inspector_name: v })} testId={`sorting-form-inspector-${i}`} placeholder="Name" /></Td>
                    <Td><RowNum value={e.total_qty} onChange={(v) => updateEntry(i, { total_qty: v })} testId={`sorting-form-total-${i}`} color="#9ec3ec" /></Td>
                    <Td><RowNum value={e.ok_qty} onChange={(v) => updateEntry(i, { ok_qty: v })} testId={`sorting-form-ok-${i}`} color="#4ed084" /></Td>
                    <Td><RowNum value={e.nok_qty} onChange={(v) => updateEntry(i, { nok_qty: v })} testId={`sorting-form-nok-${i}`} color="#E24B4A" /></Td>
                    <Td><RowNum value={e.rework_qty} onChange={(v) => updateEntry(i, { rework_qty: v })} testId={`sorting-form-rew-${i}`} color="#f3b14a" /></Td>
                    <Td>
                      <button type="button" onClick={() => setBreakdownIdx(i)}
                        className="px-2 py-0.5 rounded"
                        style={{ background: "rgba(155,89,182,0.16)", border: "1px solid rgba(155,89,182,0.55)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer", whiteSpace: "nowrap" }}
                        data-testid={`sorting-form-breakdown-btn-${i}`}>
                        {Object.keys(e.defects || {}).length > 0
                          ? `${Object.keys(e.defects).length} types`
                          : "Breakdown…"}
                      </button>
                    </Td>
                    {defectCols.map((d) => (
                      <Td key={d}><RowNum value={e.defects[d] ?? ""} onChange={(v) => updateDefect(i, d, v)} testId={`sorting-form-defect-${d}-${i}`} color="#c89adb" /></Td>
                    ))}
                    <Td><span style={mono(11, rateColor(rate), 700)}>{rate.toFixed(1)}%</span></Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        disabled={entries.length <= 1}
                        style={{ color: entries.length <= 1 ? "#3a3f55" : "#ff9a99", cursor: entries.length <= 1 ? "not-allowed" : "pointer", fontSize: 14, background: "transparent", border: "none", padding: 0 }}
                        data-testid={`sorting-form-remove-row-${i}`}
                        title="Remove row"
                      >✕</button>
                    </Td>
                  </tr>
                  {(totalMismatch || defectMismatch) && (
                    <tr data-testid={`sorting-form-row-warning-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <Td colSpan={8 + defectCols.length + 2}>
                        <div className="flex flex-wrap gap-3" style={{ paddingLeft: 28 }}>
                          {totalMismatch && (
                            <span
                              style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#f3b14a", letterSpacing: "0.3px" }}
                              data-testid={`sorting-form-warn-total-${i}`}
                            >
                              ⚠ OK+NOK+REW ({okNokRew}) ≠ Total ({tot})
                            </span>
                          )}
                          {defectMismatch && (
                            <span
                              style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#f3b14a", letterSpacing: "0.3px" }}
                              data-testid={`sorting-form-warn-defects-${i}`}
                            >
                              ⚠ defects ({defectsSum}) ≠ NOK ({toInt(e.nok_qty)})
                            </span>
                          )}
                        </div>
                      </Td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "rgba(255,255,255,0.04)", borderTop: "1px solid rgba(255,255,255,0.10)" }} data-testid="sorting-form-totals-row">
              <Td colSpan={3}>
                <span style={mono(9, "#9198b0", 600, "0.8px")}>
                  TOTALS · {totals.entryCount} {totals.entryCount === 1 ? "entry" : "entries"} · {totals.batches} batch(es) · {totals.inspectors} inspector(s)
                </span>
              </Td>
              <Td><span style={mono(13, "#9ec3ec", 700)} data-testid="sorting-form-total-grand">{totals.grandTotal.toLocaleString()}</span></Td>
              <Td><span style={mono(11, "#4ed084", 700)} data-testid="sorting-form-total-ok">{totals.ok.toLocaleString()}</span></Td>
              <Td><span style={mono(11, "#E24B4A", 700)} data-testid="sorting-form-total-nok">{totals.nok.toLocaleString()}</span></Td>
              <Td><span style={mono(11, "#f3b14a", 700)} data-testid="sorting-form-total-rew">{totals.rew.toLocaleString()}</span></Td>
              {defectCols.map((d) => (
                <Td key={d}>
                  <span style={mono(10, "#c89adb")} data-testid={`sorting-form-total-defect-${d}`}>
                    {totals.defectsByType[d] || 0}
                  </span>
                </Td>
              ))}
              <Td><span style={mono(13, rateColor(totals.rate), 700)} data-testid="sorting-form-total-rate">{totals.rate.toFixed(1)}%</span></Td>
              <Td> </Td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Attachments */}
      <div className="mt-4 grid grid-cols-2 gap-3" data-testid="sorting-form-attachments">
        <div>
          <input ref={photoRef} type="file" accept="image/*,.heic,.heif" multiple onChange={onPhotoPick} style={{ display: "none" }} data-testid="sorting-form-photo-input" />
          <button
            type="button" onClick={() => photoRef.current?.click()} disabled={photos.length >= MAX_PHOTOS}
            className="w-full px-2 py-2 rounded"
            style={{ background: "rgba(24,95,165,0.08)", border: "1px dashed rgba(24,95,165,0.35)", color: photos.length >= MAX_PHOTOS ? "#6b7388" : "#9ec3ec", fontFamily: "DM Mono, monospace", fontSize: 10, cursor: photos.length >= MAX_PHOTOS ? "not-allowed" : "pointer" }}
            data-testid="sorting-form-photo-btn"
          >
            📷 Add photos ({photos.length}/{MAX_PHOTOS})
          </button>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {photos.map((f, i) => (
                <div key={`${f.name}-${i}`} style={{ background: "rgba(78,208,132,0.10)", border: "1px solid rgba(78,208,132,0.30)", fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9aebc2", padding: "1px 6px", borderRadius: 3, display: "flex", alignItems: "center", gap: 3 }} data-testid={`sorting-form-photo-chip-${i}`}>
                  <span style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  <button type="button" onClick={() => removePhoto(i)} style={{ color: "#ff9a99", fontSize: 11, cursor: "pointer", padding: "0 2px", background: "transparent", border: "none" }} data-testid={`sorting-form-photo-remove-${i}`}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <input ref={pdfRef} type="file" accept=".pdf,application/pdf" onChange={onPdfPick} style={{ display: "none" }} data-testid="sorting-form-pdf-input" />
          <button
            type="button" onClick={() => pdfRef.current?.click()}
            className="w-full px-2 py-2 rounded"
            style={{ background: "rgba(155,89,182,0.08)", border: "1px dashed rgba(155,89,182,0.35)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 10, cursor: "pointer" }}
            data-testid="sorting-form-pdf-btn"
          >
            📄 {reportPdf ? "Replace report PDF" : "Attach report PDF"}
          </button>
          {reportPdf && (
            <div className="mt-1.5 flex items-center justify-between px-2 py-1 rounded" style={{ background: "rgba(155,89,182,0.10)", border: "1px solid rgba(155,89,182,0.30)", fontFamily: "DM Mono, monospace", fontSize: 9, color: "#c89adb" }} data-testid="sorting-form-pdf-chip">
              <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reportPdf.name}</span>
              <button type="button" onClick={() => setReportPdf(null)} style={{ color: "#ff9a99", fontSize: 12, cursor: "pointer", padding: "0 2px", background: "transparent", border: "none" }} data-testid="sorting-form-pdf-remove">×</button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="sorting-form-error">
          {error}
        </div>
      )}

      {/* Iter56 — chain a material movement request */}
      <div className="mt-4 p-2.5 rounded" style={{ background: "rgba(155,89,182,0.06)", border: "1px solid rgba(155,89,182,0.30)" }} data-testid="sorting-form-movement-block">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="sorting-form-movement-toggle-label">
          <input
            type="checkbox"
            checked={requestMovement}
            onChange={(e) => setRequestMovement(e.target.checked)}
            data-testid="sorting-form-movement-toggle"
            style={{ accentColor: "#c89adb", cursor: "pointer" }}
          />
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#c89adb", letterSpacing: "0.3px" }}>
            Also request material movement (pickup of sorted material from sorting area)
          </span>
        </label>
      </div>

      {moveModalPrefill && (
        <NewMaterialMovementModal
          prefill={moveModalPrefill}
          onClose={() => {
            const cb = moveModalPrefill._onClosed;
            setMoveModalPrefill(null);
            cb?.();
          }}
          onCreated={() => {
            const cb = moveModalPrefill._onClosed;
            setMoveModalPrefill(null);
            cb?.();
          }}
        />
      )}

      {breakdownIdx !== null && entries[breakdownIdx] && (
        <DefectBreakdownModal
          initial={entries[breakdownIdx].defects || {}}
          nokQty={entries[breakdownIdx].nok_qty}
          reworkQty={entries[breakdownIdx].rework_qty}
          knownTypes={defectCols && defectCols.length ? defectCols : undefined}
          rowLabel={entries[breakdownIdx].batch_number ? `Batch ${entries[breakdownIdx].batch_number}` : `Row ${breakdownIdx + 1}`}
          onClose={() => setBreakdownIdx(null)}
          onSave={(map) => {
            updateEntry(breakdownIdx, { defects: map });
            setBreakdownIdx(null);
          }}
        />
      )}

      <div className="mt-4 flex justify-end gap-2">
        {onCancel && (
          <button
            type="button" onClick={onCancel}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="sorting-form-cancel-btn"
          >
            Cancel
          </button>
        )}
        <button
          type="button" onClick={submit} disabled={busy}
          className="px-3 py-2 rounded-md"
          style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
          data-testid="sorting-form-submit-btn"
        >
          {busy ? "Submitting…" : `Submit ${totals.entryCount} ${totals.entryCount === 1 ? "entry" : "entries"}`}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const DefectColAdder = ({ onAdd, existing }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const available = CANONICAL_DEFECTS.filter((d) => !existing.includes(d));
  return (
    <div className="relative" data-testid="sorting-form-defect-adder">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="px-2.5 py-1 rounded"
        style={{ background: "rgba(155,89,182,0.16)", border: "1px solid rgba(155,89,182,0.55)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "0.4px", cursor: "pointer" }}
        data-testid="sorting-form-add-defect-btn"
      >
        ＋ Add defect type
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 rounded-md p-2"
          style={{ background: "var(--bg2, #0d0f18)", border: "1px solid rgba(155,89,182,0.40)", minWidth: 220, zIndex: 100 }}
          data-testid="sorting-form-defect-menu"
        >
          {available.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {available.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => { onAdd(d); setOpen(false); }}
                  style={{ background: "rgba(155,89,182,0.10)", border: "1px solid rgba(155,89,182,0.30)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 9, padding: "2px 6px", borderRadius: 3, cursor: "pointer" }}
                  data-testid={`sorting-form-defect-option-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom type…"
              className="flex-1 px-2 py-1 rounded outline-none"
              style={inputStyle()}
              data-testid="sorting-form-defect-custom-input"
            />
            <button
              type="button"
              onClick={() => { if (custom.trim()) { onAdd(custom); setCustom(""); setOpen(false); } }}
              className="px-2 py-1 rounded"
              style={{ background: "#185FA5", color: "#fff", fontFamily: "DM Mono, monospace", fontSize: 10, cursor: "pointer", border: "none" }}
              data-testid="sorting-form-defect-custom-add"
            >＋</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const Th = ({ children }) => (
  <th style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "1px", color: "#9198b0", textTransform: "uppercase", textAlign: "left", padding: "6px 8px" }}>{children}</th>
);

const Td = ({ children, colSpan }) => (
  <td colSpan={colSpan} style={{ padding: "6px 8px", verticalAlign: "middle" }}>{children}</td>
);

const RowInput = ({ value, onChange, testId, placeholder }) => (
  <input
    type="text" value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full px-1.5 py-1 rounded outline-none"
    style={inputStyle(11)}
    placeholder={placeholder}
    data-testid={testId}
  />
);

const RowNum = ({ value, onChange, testId, color }) => (
  <input
    type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full px-1.5 py-1 rounded outline-none"
    style={{ ...inputStyle(11), color, fontWeight: 600, textAlign: "right", minWidth: 60 }}
    placeholder="0"
    data-testid={testId}
  />
);

const inputStyle = (fontSize = 12) => ({
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize,
});

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, letterSpacing) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color,
  fontWeight: weight,
  letterSpacing,
});
